// /src/pages/cases/Files.tsx
import { East } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CaseIcon from "../../assets/icons/case.svg";
import UploadIcon from "../../assets/icons/file.svg";
import Button from "../../components/buttons/Button";
import FileListWithProgress from "../../components/file-management/FileListWithProgress";
import FileUploadZone from "../../components/inputs/FileUpload/FileUploadZone";
import { FileUpload } from "../../components/inputs/types";
import BottomBar from "../../components/misc/BottomBar";
import { useAppContext } from "../../contexts/AppContext";
import { useCase } from "../../contexts/CaseContext";
import { useError } from "../../contexts/ErrorContext";
import lang from "../../lang/en/pages.json";
import Main from "../../layouts/Main";
import { CasesMockResponse, FilesMockResponse } from "../../onboarding/mock";
import { useCaseManagement, useFileUpload, usePresignPreview } from "./hooks/useCaseManagement";
import { CaseFile, CreateCaseError, CreateRevisionError } from "./types";
import { showUploadResult, removeFirstByName } from "./utils";
import { UNIFIED_UPLOAD_ENABLED } from "../../config";
import { useUpload } from "../../upload/useUpload";
import UploadResultsPanel from "../../upload/UploadResultsPanel";

const FilesV2: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const { id: caseId } = useParams();
  const { isLoading, setBreadcrumbs } = useAppContext();
  const { caseMeta } = useCase() || {};
  const [selectedFiles, setSelectedFiles] = useState<(FileUpload | CaseFile)[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const controller = useUpload();
  const { fetchCaseFiles } = useFileUpload();
  const getPreviewUrl = usePresignPreview(caseId);

  const handleRemoveFile = (filename: string) => {
    const updated = removeFirstByName(selectedFiles, filename);
    setSelectedFiles(updated);
  };

  useEffect(() => {
    setBreadcrumbs([
      {
        label: lang.common.header.breadcrumb.items[0].label,
        href: "/cases",
        icon: <img src={CaseIcon} />,
      },
      { label: caseMeta?.name ?? "" },
    ]);
  }, [caseMeta]);

  useEffect(() => {
    if (!caseId) {
      setIsLoadingFiles(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoadingFiles(true);
      try {
        const existing = await fetchCaseFiles(caseId);
        if (!cancelled) setSelectedFiles(existing ?? []);
      } catch {
        // non-fatal: page still usable without existing files
      } finally {
        if (!cancelled) setIsLoadingFiles(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [caseId, fetchCaseFiles]);

  const handleAnalyze = async () => {
    if (!caseId) return;
    if (selectedFiles.length === 0) {
      showError(lang.pages.new_case_files_page.messages.nofiles);
      return;
    }
    const files = selectedFiles
      .filter((f): f is FileUpload => "file" in f && f.file instanceof File)
      .map((f) => f.file as File);
    if (files.length === 0) return;
    const existing_file_ids = selectedFiles
      .filter((f): f is CaseFile => !("file" in f))
      .map((f) => f.id);
    try {
      await controller.start({ intent: "APPEND_REVISION", case_id: caseId, files, existing_file_ids });
    } catch {
      showError("Upload could not be started. Please try again.");
    }
  };

  useEffect(() => {
    if (
      controller.session?.status === "COMPLETED" &&
      caseId
    ) {
      navigate(`/cases/${caseId}/status`);
    }
  }, [controller.session?.status, caseId, navigate]);

  const handleCancel = () => {
    controller.reset();
    if (caseId) navigate(`/cases/${caseId}`);
  };

  const hasNewFiles = selectedFiles.some(
    (f): f is FileUpload => "file" in f && f.file instanceof File
  );
  const canAnalyze = !isLoading && !controller.isRunning && hasNewFiles;

  return (
    <Main id="files-v2">
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 4, maxWidth: 860, margin: "auto", gap: 2 }}>
        <Stack alignItems="center" textAlign="center" spacing={2}>
          {caseMeta?.status === "error" && (
            <Alert sx={{ alignItems: "center" }} severity="error">
              {caseMeta.status_message}
            </Alert>
          )}
          <img src={UploadIcon} alt="Upload Icon" />
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#266A4A" }}>
            {lang.pages.files_page.header_block.title}
          </Typography>
          <Typography variant="body2" fontWeight="light">
            {lang.pages.files_page.header_block.content}
          </Typography>
        </Stack>

        <FileUploadZone
          disabled={isLoading || controller.isRunning}
          isLoading={isLoading}
          isLoadingFiles={isLoadingFiles}
          acceptZip
          panelMode="v2"
          value={selectedFiles}
          onChange={setSelectedFiles}
          buttonPosition="top"
          title={lang.pages.files_page.upload_block.title}
          description={lang.pages.files_page.upload_block.content}
          buttonText={lang.pages.files_page.upload_block.button}
          renderFileList={() => (
            <UploadResultsPanel
              controller={controller}
              pendingFiles={selectedFiles}
              onRemoveFile={handleRemoveFile}
              onResubmit={handleAnalyze}
              getPreviewUrl={getPreviewUrl}
            />
          )}
        />
        {!isLoadingFiles && !controller.isRunning && selectedFiles.length > 0 && !hasNewFiles && (
          <Typography variant="caption" sx={{ color: "#5B6B61", textAlign: "center" }}>
            {lang.pages.files_page.messages.add_new_file_hint}
          </Typography>
        )}
      </Box>

      <BottomBar
        id="analyze-v2"
        buttons={[
          <Button
            disabled={isLoading || controller.isRunning}
            sx={{ color: "black", fontWeight: "light" }}
            onClick={handleCancel}
            key={1}
            variant="text"
          >
            {lang.pages.files_page.buttons.cancel}
          </Button>,
          <Button
            sx={{ px: 4 }}
            onClick={handleAnalyze}
            key={0}
            disabled={!canAnalyze}
            variant="green"
          >
            {controller.isRunning ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              <Stack direction="row" alignItems="center" gap={1}>
                <East />
                {lang.pages.files_page.buttons.analyze}
              </Stack>
            )}
          </Button>,
        ]}
      />
    </Main>
  );
};

const FilesV1: React.FC = () => {
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [updatedFiles, setUpdatedFiles] = useState<(FileUpload | CaseFile)[]>(
    []
  );
  const navigate = useNavigate();
  const { showError, showSuccess } = useError();
  const { uploadAll, fetchCaseFiles, files, setFiles } = useFileUpload();
  const { createRevision } = useCaseManagement();
  const { id: caseId, revisionId } = useParams();

  const parsedRevisionId = Number(revisionId);
  const getPreviewUrl = usePresignPreview(
    caseId,
    Number.isFinite(parsedRevisionId) ? parsedRevisionId : undefined
  );
  const { isLoading, setBreadcrumbs } = useAppContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { caseMeta } = useCase() || {};
  const [isExample, setIsExample] = useState(false);
  const [shouldShowFiles, setShouldShowFiles] = useState(false);
  const [currentRevisionId, setCurrentRevisionId] = useState<number | null>(
    null
  );
  const [fileProgress, setFileProgress] = useState<Record<string, number>>({});

  useEffect(() => {
    setUpdatedFiles(files);
  }, [files]);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: lang.common.header.breadcrumb.items[0].label,
        href: "/cases",
        icon: <img src={CaseIcon} />,
      },
      {
        label: caseMeta?.name ?? "",
      },
    ]);
  }, [caseMeta]);

  useEffect(() => {
    console.log("Files component mounted", { caseId, revisionId });
    if (!caseId) return;
    const load = async () => {
      setIsLoadingFiles(true);
      try {
        const filesResponse = await fetchCaseFiles(
          caseId,
          revisionId ? Number(revisionId) : undefined
        );
        setFiles(filesResponse);

        const isExampleCase =
          caseMeta?.status === undefined ||
          CasesMockResponse.some((c) => c.id === caseId);
        setIsExample(isExampleCase);

        const newFiles = [...filesResponse];

        if (isExampleCase) {
          if (shouldShowFiles) {
            setFiles(FilesMockResponse);
          }
          setFiles([]);
          return;
        }
        setFiles(newFiles);
      } catch (error) {
        console.error("Error loading files", error);
        showError("Failed to load files");
      } finally {
        setIsLoadingFiles(false);
      }
    };
    load();
  }, [caseId, revisionId, caseMeta, shouldShowFiles]);

  useEffect(() => {
    const handleOnboardingStepChange = (
      event: CustomEvent<{ showMockFiles: boolean }>
    ) => {
      if (isExample) {
        setShouldShowFiles(event.detail.showMockFiles ?? false);
      }
    };

    window.addEventListener(
      "onboardingStepChange",
      handleOnboardingStepChange as EventListener
    );
    return () =>
      window.removeEventListener(
        "onboardingStepChange",
        handleOnboardingStepChange as EventListener
      );
  }, [isExample]);

  const onFileChange = (newFiles: (FileUpload | CaseFile)[]) => {
    setUpdatedFiles(newFiles);
  };

  const handleAnalyze = async () => {
    if (!caseId) {
      return;
    }
    if (isExample) {
      return;
    }
    setIsSubmitting(true);

    try {
      const newRevisionId = currentRevisionId || (await createRevision(caseId));
      setCurrentRevisionId(newRevisionId);

      const result = await uploadAll(caseId, newRevisionId, updatedFiles, handleProgress);

      showUploadResult(result, lang.pages.files_page.messages.upload_success, showSuccess);
      navigate(`/cases/${caseId}/status`);
    } catch (error) {
      console.error(
        `Error during file upload: ${error}, caseId: ${caseId}, revisionId: ${revisionId}`
      );
      setCurrentRevisionId(null);
      if (error instanceof CreateCaseError) {
        showError(error.message);
      } else if (error instanceof CreateRevisionError) {
        showError(error.message);
      } else {
        console.error("Unexpected error during file upload", error);
        const message =
          error instanceof Error ? error.message : "An unexpected error occurred while uploading files";
        showError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const didFilesChange = useMemo(() => {
 
    return (
      updatedFiles.length !== files.length ||
      updatedFiles.some((file: FileUpload | CaseFile, index) => {
        let name = "";
        let dstName = "";
        if (
          "file" in file &&
          (file as any).file &&
          typeof (file as any).file.name === "string"
        ) {
          name = (file as any).file.name;
        } else {
          name = (file as CaseFile).filename;
        }

        if (
          "file" in files[index] &&
          (files[index] as any).file &&
          typeof (files[index] as any).file.name === "string"
        ) {
          dstName = (files[index] as any).file.name;
        } else {
          dstName = (files[index] as CaseFile).filename;
        }

        console.info(`${name} !== ${dstName}`);

        return name !== dstName;
      })
    );
  }, [updatedFiles, files]);

  const handleProgress = (file: File, progress: number) => {
    console.info(`File ${file.name} upload progress: ${progress}%`);
    setFileProgress((prev) => ({
      ...prev,
      [file.name]: progress,
    }));
  };

  const handleCancel = () => {
    if (!caseId) {
      return;
    }
    navigate(`/cases/${caseId}`);
  };

  return (
    <Main id="files">
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          padding: 4,
          maxWidth: 800,
          margin: "auto",
          gap: 2,
        }}
      >
        <Stack alignItems={"center"} textAlign={"center"} spacing={2}>
          {caseMeta?.status === "error" && (
            <Alert sx={{ alignItems: "center" }} severity="error">
              {caseMeta.status_message}
            </Alert>
          )}
          <img src={UploadIcon} alt="Upload Icon" />
          <Typography
            variant="h5"
            fontWeight={"bold"}
            sx={{ color: "#266A4A" }}
          >
            {`${lang.pages.files_page.header_block.title}`}
          </Typography>
          <Typography variant={"body2"} fontWeight={"light"}>
            {lang.pages.files_page.header_block.content}
          </Typography>
        </Stack>

        <FileUploadZone
          disabled={isLoading || isSubmitting}
          isLoading={isLoading}
          isLoadingFiles={isLoadingFiles}
          acceptZip
          onChange={onFileChange}
          initialFiles={files as CaseFile[]}
          buttonPosition="bottom"
          title={lang.pages.files_page.upload_block.title}
          description={lang.pages.files_page.upload_block.content}
          buttonText={lang.pages.files_page.upload_block.button}
          renderFileList={(files) => (
            <FileListWithProgress
              files={updatedFiles}
              progressMap={fileProgress}
              disabled={isLoading || isSubmitting}
              getPreviewUrl={getPreviewUrl}
              onChange={setUpdatedFiles}
              onClearProgressKey={(key) =>
                setFileProgress((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                })
              }
            />
          )}
        />
      </Box>
      <BottomBar
        id={"analyze"}
        buttons={[
          <Button
            disabled={isLoading || isSubmitting}
            sx={{ color: "black", fontWeight: "light" }}
            onClick={handleCancel}
            key={1}
            variant="text"
          >
            {lang.pages.files_page.buttons.cancel}
          </Button>,
          <Button
            sx={{ px: 4 }}
            onClick={handleAnalyze}
            key={0}
            disabled={isLoading || isSubmitting || !didFilesChange}
            variant="green"
          >
            {isSubmitting ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <East />
                {lang.pages.files_page.buttons.analyze}
              </Stack>
            )}
          </Button>,
        ]}
      />
    </Main>
  );
};

const Files: React.FC = () => {
  if (UNIFIED_UPLOAD_ENABLED) return <FilesV2 />;
  return <FilesV1 />;
};

export default Files;
